import { POrtho, PSquare } from "../widgets/Delimit";
import { Expando } from "../widgets/Expando";
import { MultiExpando } from "../widgets/MultiExpando";
import { Rule } from "../widgets/Rule";
import { RulesTable } from "../widgets/RulesTable";
import { Say } from "../widgets/Say";

/** expandable help that explains how to use the Orthography area */
export function HelpOrtho() {
  return (
    <div className="prime-help-div">
      <MultiExpando title="show help:"
        labels={['overview', 'samples', 'custom keyboard', 'making rules']}
        expanded={[false, false, false, false]}>
        {[
          <span key="overview" className="prime-help-body">
            <div className='prime-help-title'>Overview</div>
            <Overview />
            <hr />
          </span>,
          <span key="samples" className="prime-help-body">
            <div className='prime-help-title'>Samples</div>
            <Samples />
            <hr />
          </span>,
          <span key="custom keyboard" className="prime-help-body">
            <div className='prime-help-title'>Custom keyboard</div>
            <AssigningKeys />
            <hr />
          </span>,
          <span key="making rules" className="prime-help-body">
            <div className='prime-help-title'>Making rules</div>
            <MakingRules />
            <hr />
          </span>,
        ]}
      </MultiExpando>
    </div>
  );
}

function Overview() {
  return (<>
    <p>
      This tool allows you to map keys on your keyboard to a custom orthography (set of symbols for your language),
      create rules to describe how to convert that orthography into IPA
      (the <a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet">International Phonetic Alphabet</a>),
      and hear it spoken by a speech synthesizer.
    </p>
    <ol>
      <li>Pick <b>show help: samples</b> to learn about the built-in samples of custom keyboards and orthography-to-IPA rules.</li>
      <li>Pick <b>show help: custom keyboard</b> to learn how to map keystrokes to custom symbols in your orthography.</li>
      <li>Pick <b>show help: making rules</b> to learn how to create rules that convert your orthography into IPA.</li>
      <li>Once you have a custom keyboard and custom conversion rules, you can type words from your language
        into the <b>input text</b> box, see the corresponding IPA that describes the pronunciation in the <b>output IPA</b> box,
        then pick <b>speak output</b> to hear the results.</li>
    </ol>
    <p>
      This tool is aimed at orthographies with dozens of symbols, such as an alphabet or syllabary.
      Also note that it's limited to the symbols in your system's fonts.
    </p>
  </>);
}

function Samples() {
  return (<>
    <p>
      There are several built-in samples to give you an idea of how the system works.
      Note that the custom keyboard samples assume a standard English keyboard,
      so you may want to adjust the mappings if you have a different keyboard layout.
      They also depend on the your system using a font that supports these scripts.
    </p>
    <p>
      Click on any of the sample names below to learn more.
    </p>

    <div style={{ display: "flex", justifyContent: "left" }}>
      <Expando label={<b>Ancient Greek</b>}>
        <>
          <p>
            This example demonstrates mapping to the Greek letters as an alternate orthography.
            For more information on ancient Greek, see <a href="https://en.wikipedia.org/wiki/Greek_alphabet#Letters">link</a>.
          </p>
          <p>
            This illustrates a fairly straightforward mapping from keystrokes
            to custom orthography (the Greek alphabet) to phonemes.
            There are a few cases where the mapping from key to orthography is ambiguous
            (e.g., <i>e</i> could be <POrtho>ε</POrtho> or<POrtho>η</POrtho>), which is handled by mapping the lowercase letter to
            one symbol (e.g., <Rule>e: ε</Rule>) and mapping the uppercase letter to multiple
            alternatives, including the capitalized versions (e.g., <Rule>E: Ε, η, or Η</Rule>).
          </p>
          <p>
            The orthography to phoneme rules are also straightforward, with the
            exception of a couple symbols that depend on the symbol that follows them.
            These are handled at the beginning of the rules
            (e.g., <Rule>σ &gt; z / _&#123;β | γ | μ&#125;</Rule>),
            before the other symbols have been converted to IPA.
          </p>
        </>
      </Expando>
    </div>

    <div style={{ display: "flex", justifyContent: "left" }}>
      <Expando label={<b>Esperanto</b>}>
        <>
          <p>
            Esperanto is a constructed language that uses the Latin alphabet with a few additions like ĉ.
            For more information on Esperanto, see <a href="https://en.wikipedia.org/wiki/Esperanto#Orthography">link</a>.
          </p>
          <p>
            This illustrates a case where most of the orthography is shared with English,
            with a small number of overrides needed for the additional symbols and their capitals
            (e.g., <Rule>C: C, ĉ, or Ĉ</Rule>).
          </p>
          <p>
            Similarly, many of the symbols match the phonemes of the corresponding IPA, so they don't need rules.
            Most of the remaining symbols have a simple mapping to IPA.
            The only symbol with a conditional phoneme <POrtho>n</POrtho> runs its rule after the phonemes it
            depends on have been converted to IPA (e.g., the rule <Rule>ĥ &gt; x</Rule> is before
            the rule <Rule>n &gt; ŋ / _&#123;ɡ|k|x&#125;</Rule>, which depends on it).
          </p>
        </>
      </Expando>
    </div>

    <div style={{ display: "flex", justifyContent: "left" }}>
      <Expando label={<b>Futhorc</b>}>
        <>
          <p>
            This is a simple adaptation of Anglo-Saxon runes, where the name <i>futhorc</i> refers to the first
            six letters of the runic alphabet: ᚠᚢᚦᚩᚱᚳ.
            For more information on futhorc, see <a href="https://en.wikipedia.org/wiki/Anglo-Saxon_runes#Rune_inventory">link</a>.
          </p>
          <p>
            For the custom keyboard, most English keys turn directly into the obvious rune (e.g., <Rule>b: ᛒ</Rule>).
            When there's more than one possibility, it leverages the closest capital letter (e.g., <Rule>e: ᛖ</Rule> and <Rule>E: ᛠ</Rule>).
            Everything is chosen so that you can create any rune with a single letter.
          </p>
          <p>
            Similarly, the rune-to-IPA rules are fairly simple.
            The one additional detail is that it changes several voiceless consonants to voiced between vowels,
            so it first converts all the vowels so that the context-sensitive rules can rely on IPA vowels being recognized,
            e.g., <Rule>ᚪ &gt; ɑː</Rule> happens before <Rule>ᚠ &gt; v / V_V</Rule>, which happens before <Rule>ᚠ &gt; f</Rule>.
          </p>
          <p>
            It also has special handling of where it puts stress.
            If a word starts with <PSquare>for</PSquare> or <PSquare>je</PSquare> followed by a consonant,
            it will add stress to the second syllable, which is described by the rule <Rule>2 &#123;#,&#123;for|&#123;je,C&#125;&#125;&#125;</Rule>.
            If the stress rule isn't matched, it defaults to adding stress to the first syllable.
          </p>
        </>
      </Expando>
    </div>

    <div style={{ display: "flex", justifyContent: "left" }}>
      <Expando label={<b>Hiragana</b>}>
        <>
          <p>
            Hiragana is one of the Japanese syllabaries, with many more symbols than there are letters in the English alphabet.
            For more information on Hiragana, see <a href="https://en.wikipedia.org/wiki/Hiragana#Table_of_hiragana">link</a>.
          </p>
          <p>
            To account for the large number of symbols, the custom keyboard is organized by the initial consonant in the syllables.
            The symbols attached to each key are sorted by the vowel that follows, typically&nbsp;
            <PSquare>a</PSquare>, <PSquare>i</PSquare>, <PSquare>ɯ</PSquare>, <PSquare>e</PSquare>, <PSquare>o</PSquare>, <PSquare>ʲa</PSquare>, <PSquare>ʲɯ</PSquare>, <PSquare>ʲo</PSquare>.
            For example, <Rule>B: ば び ぶ べ ぼ びゃ びゅ びょ</Rule>,
            where the corresponding syllables are <Say>[ba]</Say>, <Say>[bi]</Say>, <Say>[bɯ]</Say>,
            <Say>[be]</Say>, <Say>[bo]</Say>, <Say>[bʲa]</Say>, <Say>[bʲɯ]</Say>, and <Say>[bʲo]</Say>.
          </p>
          <p>
            The base rules mostly just do a simple conversion from symbol to the IPA for the corresponding syllable,
            including some punctuation.
            A couple symbols to call out are ん and っ:
          </p>
          <ul>
            <li>
              Since ん is a <a href="https://en.wikipedia.org/wiki/Mora_(linguistics)">mora</a>,
              it's converted to syllabic <PSquare>n̩</PSquare> to help differentiate words such
              as <Say say="[kin̩en̩]">きんえん [kin̩en̩]</Say> versus <Say say="[kinen̩]">きねん [kinen̩]</Say>.
            </li>
            <li>
              っ typically indicates that the next consonant is longer.
              But since IPA puts the lengthen symbol (ː) after (such as <PSquare>ɕː</PSquare>) rather than before (such as <POrtho>っし</POrtho>),
              the rule needs to indicate that the next symbol needs to be swapped with it,
              which looks like this: <Rule>っ &gt; ː *(next)</Rule>.
              And lengthening the affricate <PSquare>t͡ɕ</PSquare> has the effect of adding a <PSquare>t</PSquare>,
              thus we have the rule <Rule>っ &gt; t / _t͡ɕ</Rule>.
              These two rules give us <Say say="[d͡zaɕːi]">ざっし [d͡zaɕːi]</Say> and <Say say="[kott͡ɕi]">こっち [kott͡ɕi]</Say>.
            </li>
          </ul>
          <p>
            Note that each set of rules will only change each symbol a maximum of once,
            so if we want to make additional (possibly context-sensitive) changes to the phonemes,
            we need a second set of rules.
            For example, the hiragana-to-IPA rules may give us <PSquare>oɯ</PSquare>, which is typically pronounced <PSquare>oː</PSquare>.
            This change happens in the second set of rules as <Rule>oɯ &gt; oː</Rule>.
            The result is that とうきょう is pronounced as <Say>[toːkʲoː]</Say> rather than <Say>[toɯkʲoɯ]</Say>.
            Note that you'd want to add additional rules to make it sound more like spoken Japanese.
          </p>
        </>
      </Expando>
    </div>

    <div style={{ display: "flex", justifyContent: "left" }}>
      <Expando label={<b>Pinyin</b>}>
        <>
          <p>
            Pinyin, aka the Chinese Phonetic Alphabet, uses modified Latin symbols to phonetically describe Standard Chinese (Mandarin).
            This example manages tones, demonstrates multiple sets of rules, and includes vowel lengths.
            For more information on Pinyin,
            see <a href="https://en.wikipedia.org/wiki/Pinyin">here</a> and <a href="https://www.fluentu.com/blog/chinese/learn-pinyin/">here</a>.
          </p>
          <p>
            Pinyin uses the same letters as English (except for <i>v</i>) so the custom keyboard
            doesn't need to translate most keystrokes (such as <i>a</i> and <i>b</i>).
            But Pinyin also includes vowels with diacritics to represent tones and distinct vowel sounds, so these have keyboard shortcuts.
            Since the four tones are usually referred to by number,
            the mapping has the numbers 1-4 add the diacritic that corresponds to the numbered tone.
            For example, typing <i>a1</i> gives you <i>ā</i>, which is <i>a</i> using the first tone.
            There's also the additional vowel symbol <i>ü</i>.
            In order to support this plus capital <i>U</i>, <i>U</i> is mapped to <i>U ü</i> (<i>ü</i> can't
            appear at the start of a word so it doesn't need a capital version).
          </p>
          <p>
            The rules use ¹²³⁴ to represent the four main tones of Mandarin, which are ˥, ˧˥, ˨˩˦, and ˥˩ in IPA.
            Since the numbers are easier to understand, it uses them throughout the rules, only converting to IPA tones right at the end.
            Early rules convert to numbers - e.g., <Rule>á &gt; a²</Rule> - rules in the middle may reference
            the numbers - e.g., <Rule>a²i &gt; ai̯²</Rule> - and rules at the end convert the tones to IPA - e.g., <Rule>² &gt; ˧˥</Rule>.
            Having a single place where the IPA tones are specified also makes it easier to tweak the tones if desired.
          </p>
          <p>
            There are a couple details to note in the rule <Rule>a²i &gt; ai̯²</Rule>.
            One is that it explicitly marks <PSquare>ai</PSquare> as a diphthong (vowel that transitions from <PSquare>a</PSquare> to <PSquare>i</PSquare>)
            by adding the diacritic under the <PSquare>i̯</PSquare> that means <i>extra short</i>.
            Two is that it moves the tone to the end of the diphthong to help the speech synthesizer: <Say say="[ai̯300˧˥]">[ai̯²]</Say>.
          </p>
          <p>
            You will also notice rules that check for each of the tone numbers, such as <Rule>yua &gt; ɥɛ / _&#123;n|¹n|²n|³n|⁴n&#125;</Rule>.
            This means <i>change <POrtho>yua</POrtho> to <PSquare>ɥɛ</PSquare> when followed by n, ¹n, ²n, ³n, or ⁴n</i>.
            It needs to check each of the tones (or absence of tone) because pinyin may contain yuan, yua¹n, yua²n, yua³n, or yua⁴n.
          </p>
          <p>
            Another complication these rules handle is that symbols that look the same can be constructed in different ways.
            For example, <i>á</i> can be a single character (Unicode 00E1) or <i>á</i> can be an <i>a</i> (Unicode 0061) followed by
            a diacritic called an acute accent (Unicode 0301).
            The rules convert the single characters to a letter and number, e.g., <Rule>á &gt; a²</Rule>,
            and convert the diacritics to tone numbers, e.g., <Rule>ˊ &gt; ²</Rule>, leaving the letters as-is.
          </p>
          <p>
            Note that a single set of rules will only change each symbol once.
            When you're converting from an orthography (like pinyin) to IPA where both share symbols (like <i>e</i>),
            this makes it easier to avoid having rules that collide, with a later rule changing something that was already correct.
            For example, there's a rule that says <Rule>yue &gt; ɥe</Rule> followed by a later rule that says <Rule>e &gt; ɤ</Rule>,
            which shouldn't be run on any <i>e</i> produced by the first rule.
          </p>
          <p>
            When the rules listed under <i>rules2 (second pass):</i> are run, it starts out by resetting the symbol tracking so they can be changed again.
            For example, the first set of rules contains <Rule>á &gt; a²</Rule> while the second set of rules
            contains <Rule>a²i &gt; ai̯²</Rule>, which modifies the results of the first rule.
            If you need to reset the symbols again, you can explicitly add <Rule>reset</Rule> in the <i>rules2</i> section.
            The pinyin rules do this right before the final pass of converting the tones and tweaking the diphthongs.
            Here are what the different passes do:
          </p>
          <ul>
            <li><b>pass 1:</b> converts tone markers from single characters or diacritics, converts miscellaneous non-IPA consonants and punctuation to IPA</li>
            <li><b>pass 2:</b> handles vowel combinations, moves tones to the end of diphthongs, converts consonants to the appropriate IPA</li>
            <li><b>pass 3:</b> (after <Rule>reset</Rule>) converts tone numbers to IPA tones and tweaks the standard diphthongs to better match recordings -
              this pass makes it easy to adjust tones and diphthongs</li>
          </ul>
        </>
      </Expando>
    </div>

    <div style={{ display: "flex", justifyContent: "left" }}>
      <Expando label={<b>Spanish</b>}>
        <>
          <p>
            Spanish is very widely spoken, with a large number of dialects and accents.
            The built-in rules just aim for a fairly generic Spain accent.
            For more information on Spanish, see <a href="https://en.wikipedia.org/wiki/Spanish_orthography">link</a>.
          </p>
          <p>
            The custom keyboard is fairly small since Spanish only needs a few additional symbols over English:
            accented vowels, ñ, and punctuation (e.g., ¡ándale! and ¿qué?).
          </p>
          <p>
            The Spanish rules for converting letters to IPA are fairly complex,
            with careful ordering so that the expected IPA is present when a rule checks for it.
            There are also many rules that make detailed context checks before running.
          </p>
          <p>
            It includes custom rules for deciding where to put stress indicators:
          </p>
          <ul>
            <li><Rule>0 &#123;#,&#123;de|es|el|la|se|te&#125;,#&#125;</Rule> removes stress from common short words</li>
            <li><Rule>-1 &#123;&#123;V&[+non-syllabic]&#125;,#&#125;</Rule> puts stress on the last syllable
              if the word ends with a diphthong (the last phoneme is a non-syllabic vowel)</li>
            <li><Rule>-2 &#123;&#123;V|n|s&#125;,#&#125;</Rule> says to add stress to the second to last syllable
              if the word ends with a vowel, n, or s.</li>
            <li>If none of those rules is a match, the default is set to the last syllable.</li>
          </ul>
          <p>
            It also sets custom lengths for the vowels so that they're spoken fairly quickly.
          </p>
        </>
      </Expando>
    </div>
  </>);
}

function AssigningKeys() {
  return (<>
    <p>
      If your language's orthography (writing system) contains symbols not easily supported by your keyboard,
      you can make it simpler to type words in your language by
      choosing <i>assign keys</i> next to <i>input text</i> below to get an interface that lets you map keystrokes to symbols.
      Before trying out the next few steps, pick <i>assign keys</i> to enable the interface
      and make sure <i>use custom keyboard</i> is on so that you can see and use your new keyboard as you create it
      (it starts out empty).
      &nbsp;<i>Note that it's limited to the symbols in your system's fonts.</i>
    </p>
    <p>
      Let's say your orthography has <b>æ</b>, but not <b>a</b>, and you want to use <b>æ</b> whenever you type <b>a</b>.
      First, type <b>a</b> in the box under <i>key</i>, then copy-and-paste <b>æ</b> into the box under <i>symbol(s)</i>.
      Finally, pick the <i>assign</i> button to add it to your custom keyboard.
      You should now see <b>a: æ</b> above the input box and when you type <b>a</b>, it should replace it with <b>æ</b>.
      Alternatively, you can click on the <b>æ</b> button above the input box to insert it.
    </p>
    <p>
      But perhaps in addition to <b>æ</b>, your language also contains <b>ã</b> plus the capital versions
      of those letters: <b>Æ</b> and <b>Ã</b>.
      One way to support this is by attaching the additional symbols to a shift-keystroke.
      Find the empty row below <i>key/symbol(s)</i> and type <b>A</b> (shift-A) into the <i>key</i> box and
      paste <b>Æ ã Ã</b> into the <i>symbol(s)</i> box and pick <i>assign</i>.
      You should see <b>A: Æ ã Ã</b> appear above the input box.
      Now when typing into the input box, if you hold down the shift-key and hit the <i>a</i> key, <b>Æ</b> should appear.
      If you continue to hold down the shift-key and hit <i>a</i> a second time, that will be replaced with <b>ã</b>.
      If you continue to hold down the shift-key and hit <i>a</i> a third time, it will be replaced with <b>Ã</b>.
      You can attach any number of symbols to a shift-key so that holding down the shift-key and tapping the key
      will cycle between the alternate symbols.
      Or you can pick on the symbol button in the custom keyboard display to insert it.
    </p>
    <p>
      This allows you to map keystrokes to the symbols in your orthography,
      even if your orthography has more symbols than your keyboard has keys.
      Toggle <i>use custom keyboard</i> to switch between standard typing and your custom keyboard.
    </p>
    <p>
      You can change what's assigned to a key by typing into the corresponding <i>symbol(s)</i> box and picking <i>assign</i>.
      Picking <i>delete</i> will remove the key assignment.
      To reorder a key assignment, click in the <i>symbol(s)</i> box of the key you wish to move,
      then use shift-up-arrow to move it up in the list or shift-down-arrow to move it down in the list.
    </p>
    <p>
      You can save your custom key assignments for later use.
      Find the text box between the <i>save</i> and <i>delete</i> buttons, right below the <i>assign keys</i> checkbox.
      Type in the name you'd like to save it under and click the <i>save</i> button.
      Your new key assignments should now appear in the <i>keys:</i> list on the <i>load:</i> bar.
      This saves it locally in your browser.
    </p>
    <p>
      Or pick the <i>download</i> button to save it as a file in your system's <i>download</i> folder,
      which is useful for making a backup or transferring to other systems.
      To reload the file, pick the <i>load file...</i> option from the <i>keys:</i> dropdown list.
    </p>
  </>);
}

function MakingRules() {
  return (<>
    <p>
      You can create rules to convert your orthography into IPA that can be spoken.
      For example, you can say that you want <b>sc</b> to be pronounced as <Say>/ʃ/</Say>,
      that stress should be applied to the second syllable if the word starts with <Say>/je/</Say>,
      and that the vowel <Say say="[a200]">a</Say> should last approximately 200 ms.
    </p>
    <p>
      Pick on the topic you're interested in below to learn more.
      Make sure that <i>orthography to IPA rules:</i> below the <i>output IPA:</i> box
      is checked in order to see and edit the rules.
    </p>
    <MultiExpando title="details:"
      labels={['preprocessing', 'rules', 'stress', 'vowel length', 'save/load']}
      expanded={[false, false, false, false, false]}>
      {[
        <span key="preprocessing" className="prime-help-body">
          <Preprocess />
        </span>,
        <span key="rules" className="prime-help-body">
          <OrthoToIpaRules />
        </span>,
        <span key="stress" className="prime-help-body">
          <Stress />
        </span>,
        <span key="vowel length" className="prime-help-body">
          <VowelLength />
        </span>,
        <span key="save/load" className="prime-help-body">
          <SaveLoadRules />
        </span>,
      ]}
    </MultiExpando>
  </>);
}

function Preprocess() {
  return (<>
    <p><b>Preprocess:</b></p>
    <ul>
      <li>If uppercase letters should be pronounced the same as the
        lowercase version, make sure the <i>lowercase</i> option is checked.</li>
      <li>If you want punctuation such as periods and question marks to affect a sentence's intonation
        similar to the way it does in English, check the <i>punctuation</i> option.</li>
    </ul>
  </>);
}

function OrthoToIpaRules() {
  return (<>
    <p>
      <b>Rules:</b> You create rules to convert symbols in your orthography to IPA.
      For example, the rule <Rule>sc &gt; ʃ</Rule> converts the pair of letters <b>sc</b> into the IPA symbol <b>ʃ</b>.
      Type each of your rules into the <i>rules</i> box in the <i>orthography to IPA rules</i> section,
      with each rule on a separate line.
      Pick <i>use IPA keyboard</i> to see and type IPA, or pick <i>use custom keyboard</i> to see
      and type your custom orthography as defined with the <i>assign keys</i> option.
    </p>
    <p>
      Rules can also be told to run only in a specific context,
      for example <Rule>sc &gt; ʃ / _i</Rule> only does the conversion when <b>sc</b> is followed by <b>i</b>,
      where <b>/</b> indicates that the rule's <i>context</i> will follow and <b>_</b> represents the symbol being considered
      (in this case, <b>sc</b>).
    </p>
    <p>
      See <a href="?page=rules-essay">Sound Change Rules</a> for more details about how to write rules.
      The two things to be aware of are that this page currently doesn't support rules that examine
      how syllables are stressed (unless the words already have stress markers and syllable separators in them),
      and that the <i>rules2</i> section automatically does a <Rule>reset</Rule> before running.
      For example, you may have the first set of rules do a straightforward conversion from your orthography
      to IPA, with the second set of rules (rules2) refining the pronunciation based on context.
      If you need further independent sets of rules, add the line <Rule>reset</Rule> to <i>rules2</i> to
      have it reset tracking of changes.
    </p>
    <p>
      When you're ready to try out your new rules, click on the <i>use rules</i> button.
      The button turns green when the rules are applied successfully.
      If it finds a problem, it will report the issue right under the button
      and the button will turn red.
      The button will be yellow when the rules have been changed but not yet applied.
    </p>
    <p>
      Here's a quick reference of some other forms rules can use:
    </p>
    <RulesTable rules={[
      { rule: "a > b", other: <>change a into b</> },
      { rule: "a > b / c_", other: <>...when preceded by c</> },
      { rule: "a > b / _d", other: <>...when followed by d</> },
      { rule: "a > b / c_d", other: <>...when preceded by c and followed by d</> },
      { rule: "a > b / #_", other: <>...at the start of a word</> },
      { rule: "a > b / _#", other: <>...at the end of a word</> },
      { rule: "", other: <>&nbsp;</> },

      { rule: "a > b / {b|c|d}_", other: <>...when preceded by b, c, OR d</> },
      { rule: "a > b / _!b", other: <>...when NOT followed by b</> },
      { rule: "a > b / !{b|c}_", other: <>...when NOT preceded by b OR c</> },
      { rule: "", other: <>&nbsp;</> },

      { rule: "a > b *(next)", other: <>...then swap with the next phoneme</> },
      { rule: "a > b *(prev)", other: <>...then swap with the previous phoneme</> },
      { rule: "", other: <>&nbsp;</> },

      { rule: "a > b / V_C", other: <>...when preceded by a vowel and followed by a consonant</> },
      { rule: "a > b / [+voiced][+trill]_", other: <>...when preceded by a voiced trill</> },
      { rule: "a > ∅", other: <>remove a</> },
      { rule: "", other: <>&nbsp;</> },

      { rule: "reset", other: <>reset tracking of symbol changes in rules2</> },
      { rule: "// comment", other: <>useful for explaing rules, otherwise ignored</> },
    ]} />
  </>);
}

function Stress() {
  return (<>
    <p>
      <b>Stress:</b> By default, stress is added to the first syllable of each word.
      You can pick from the <i>stress on syllable</i> list to change this: <i>none</i> to not add stress,
      1 for the first syllable, 2 for the second syllable, -1 for the last syllable,
      and -2 for the second to last syllable.
    </p>
    <p>
      If you want to make stress dependent on the phonemes in a word,
      pick the <i>custom stress rules:</i> option below the rules so that it's checked.
      Here you can provide a list that describes where to add stress in different situations,
      defaulting to the <i>stress on syllable</i> option if none of the patterns match.
    </p>
    <p>
      A stress rule starts with the syllable to stress followed by a pattern a word needs to match.
      Some examples:
    </p>
    <RulesTable rules={[
      { rule: "-1 {V,#}", other: <>stress last syllable if word ends with a vowel</> },
      { rule: "2 {#,C}", other: <>stress second syllable if word starts with a consonant</> },
      { rule: "", other: <>&nbsp;</> },

      { rule: "{b|c}", other: <>b OR c</> },
      { rule: "{[+voiced]&[+fricative]}", other: <>voiced AND fricative</> },
      { rule: "!{V|ɹ}", other: <>NOT a vowel or /ɹ/</> },
      { rule: "{u,#}", other: <>/u/ followed by end-of-word,<br />meaning that the word ends with /u/</> },
      { rule: "", other: <>&nbsp;</> },

      { rule: "-1 {{V&[+non-syllabic]},#}", other: <>last syllable if word ends with a non-syllabic vowel, which typically indicates a diphthong</> },
      { rule: "-2 {{V|n|s},#}", other: <>second to last syllable if word ends with vowel, n, or s</> },
    ]} />
  </>);
}

function VowelLength() {
  return (<>
    <p>
      <b>Vowel length:</b> If you check the <i>set vowel lengths</i> option at the bottom of the page,
      you will have options for controlling the spoken lengths of vowels and diphthongs.
      These values are used internally by the speech synthesizer but don't appear in IPA.
    </p>
    <p>
      You can specify how long a vowel will last by typing in the vowel, its length (in milliseconds), and clicking <i>add</i>.
      The synthesizer supports values in the range of 50 to 580.
      Note that this is only approximate, so these numbers should be treated as <i>relative</i> values,
      with larger numbers indicating longer vowels.
    </p>
    <p>
      The vowel can be a single symbol (e.g. <b>a</b>), diphthong (e.g. <b>eɪ̯</b>),
      rhotic (e.g. <b>ɛɹ</b>), length-modified vowel (e.g. <b>aː</b>),
      or syllabic consonant (e.g. <b>ŋ̩</b>).
    </p>
    <p>
      You can change an existing value by typing in a new number and clicking the <i>change</i> button.
      Get rid of all custom lengths by clicking on the <i>clear lengths</i> button.
    </p>
  </>);
}

function SaveLoadRules() {
  return (<>
    <p>
      <b>Save/load:</b> You can save your custom rules for later use.
      Find the line that starts with the <i>orthography to IPA rules:</i> checkbox,
      then find the text box between the <i>save</i> and <i>delete</i> buttons.
      Type in the name you'd like to save the rules under and click the <i>save</i> button.
      Your new rules should now appear in the <i>rules:</i> list on the <i>load:</i> bar.
      This saves them locally in your browser.
    </p>
    <p>
      Or pick the <i>download</i> button to save them as a file in your system's <i>download</i> folder,
      which is useful for making a backup or transferring to other systems.
      To reload the file, pick the <i>load file...</i> option from the <i>rules:</i> dropdown list.
    </p>
    <p>
      Note that if your custom key assignments and rules have been saved under the same name,
      the name will appear in the <i>load:</i> dropdown list.
      Picking the item will load both the key assignments and rules together.
    </p>
  </>);
}